import type { Metric } from 'web-vitals';
import { onCLS, onFCP, onINP, onLCP, onTTFB } from 'web-vitals';

import log from 'js/app/loggerSingleton';

import { highPriorityClient, track } from '@coursera/event-pulse/sdk';

type WebVitalsMetricPayload = {
  value: number;
  rating: Metric['rating'];
  delta: number;
  navigationType: Metric['navigationType'];
  id: string;
  timestamp: number;
  navigationStart: number;
};

type WebVitalsEventPayload = {
  '[Amplitude] LCP'?: WebVitalsMetricPayload;
  '[Amplitude] FCP'?: WebVitalsMetricPayload;
  '[Amplitude] INP'?: WebVitalsMetricPayload;
  '[Amplitude] CLS'?: WebVitalsMetricPayload;
  '[Amplitude] TTFB'?: WebVitalsMetricPayload;
  '[Amplitude] Page Domain'?: string;
  '[Amplitude] Page Location'?: string;
  '[Amplitude] Page Path'?: string;
  '[Amplitude] Page Title'?: string;
  '[Amplitude] Page URL'?: string;
};

function getMetricStartTime(metric: Metric) {
  const startTime = metric.entries[0]?.startTime || 0;
  return performance.timeOrigin + startTime;
}

function getDecodeURI(locationStr: string) {
  let decodedLocationStr = locationStr;
  try {
    decodedLocationStr = decodeURI(locationStr);
  } catch (e) {
    log.error('Malformed URI sequence: ', e);
  }

  return decodedLocationStr;
}

function processMetric(metric: Metric) {
  return {
    value: metric.value,
    rating: metric.rating,
    delta: metric.delta,
    navigationType: metric.navigationType,
    id: metric.id,
    timestamp: Math.floor(getMetricStartTime(metric)),
    navigationStart: Math.floor(performance.timeOrigin),
  };
}

/**
 * Reports web vitals metrics using the high priority client.
 * Note: web vitals metrics are only reported once page's visibility changes to hidden.
 * Further improvements could me made to report metrics when client side routing happens, not sure if there is value in it.
 */
const initializeWebVitalsEventing = () => {
  const location = window.location;
  const locationHref = getDecodeURI(location?.href || '');
  let visibilityListener: ((this: Document, ev: Event) => void) | null = null;

  const webVitalsEventPayload: WebVitalsEventPayload = {
    '[Amplitude] Page Domain': location?.hostname,
    '[Amplitude] Page Location': locationHref,
    '[Amplitude] Page Path': getDecodeURI(location?.pathname || ''),
    '[Amplitude] Page Title': document?.title || '',
    '[Amplitude] Page URL': getDecodeURI(locationHref.split('?')[0] || ''),
  };

  onLCP((metric) => {
    webVitalsEventPayload['[Amplitude] LCP'] = processMetric(metric);
  });

  onFCP((metric) => {
    webVitalsEventPayload['[Amplitude] FCP'] = processMetric(metric);
  });

  onINP((metric) => {
    webVitalsEventPayload['[Amplitude] INP'] = processMetric(metric);
  });

  onCLS((metric) => {
    webVitalsEventPayload['[Amplitude] CLS'] = processMetric(metric);
  });

  onTTFB((metric) => {
    webVitalsEventPayload['[Amplitude] TTFB'] = processMetric(metric);
  });

  visibilityListener = async () => {
    // At this point all the metrics should have been collected
    if (document.visibilityState === 'hidden' && visibilityListener) {
      track(
        'web_vitals',
        {
          ...webVitalsEventPayload,
        },
        undefined,
        highPriorityClient
      );

      // Once reported, clean up
      document.removeEventListener('visibilitychange', visibilityListener);
      visibilityListener = null;
    }
  };

  /**
   * @see https://developer.mozilla.org/en-US/docs/Web/API/Document/visibilitychange_event#usage_notes
   * Note: when testing in Chrome on localhost, the visibilitychange event is not fired when navigating to a new page.
   * Prefer testing it in Firefox.
   */
  document.addEventListener('visibilitychange', visibilityListener);
};

export default initializeWebVitalsEventing;
